import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState
} from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import Lenis from 'lenis'

import { useRouterStore } from '@/store/router'

import Scrollbar from './components/Scrollbar'
import { useLenisStore } from './useLenisStore'

export const LenisContext = createContext<{ lenis?: Lenis } | undefined>(
  undefined
)

const LenisProvider = ({ children }: PropsWithChildren) => {
  const [lenis, setLenis] = useState<Lenis>()
  const setState = useLenisStore((state) => state.setState)

  const update = useCallback(
    ({ scroll, limit, progress, dimensions }: Lenis) => {
      setState({ scroll, limit, progress, dimensions })
      ScrollTrigger.update()
    },
    [setState]
  )

  useIsomorphicLayoutEffect(() => {
    const lenis = new Lenis({
      smoothWheel: true
    })

    setLenis(lenis)

    return () => {
      lenis.destroy()
      setLenis(undefined)
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (!lenis) return

    const tick = (time: number) => {
      lenis.raf(time * 1000)
    }

    gsap.ticker.add(tick)

    lenis.on('scroll', update)

    return () => {
      lenis.off('scroll', update)
      gsap.ticker.remove(tick)
    }
  }, [lenis])

  useIsomorphicLayoutEffect(() => {
    if (!lenis) return

    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      (currentRoute, previousRoute) => {
        if (currentRoute !== previousRoute) {
          lenis.scrollTo(0, { immediate: true })
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [lenis])

  useIsomorphicLayoutEffect(() => {
    window.history.scrollRestoration = 'manual'
  }, [])

  const contextValue = useMemo(() => ({ lenis }), [lenis])

  return (
    <LenisContext.Provider value={contextValue}>
      {children}
      <Scrollbar />
    </LenisContext.Provider>
  )
}

export default LenisProvider
